import { FinancialInfo, Jurisdiction, Level, Step, SubJurisdiction } from '../../../models';
import { formatSubjurisdictionName } from '../../ValidateScreen/utils';

const validateSubJurisdictionUserInput = (userInput: string, subJurisdictionsSet: Set<SubJurisdiction['name']>) => {
  return [...subJurisdictionsSet].map((subJur) => subJur.toLowerCase()).includes(userInput.toLowerCase());
};

export const generateSubJurValidationArray = (
  userInput: string[],
  subJurisdictionsSet: Set<SubJurisdiction['name']>
) => {
  const subJurisdictionValidationArray = userInput.map((v: string) =>
    validateSubJurisdictionUserInput(v, subJurisdictionsSet)
  );
  return subJurisdictionValidationArray;
};

export const filterOutInvalidStates = (stateArray: string[], subJurisdictionValidationArray: boolean[]) => {
  return stateArray.filter((_, index) => subJurisdictionValidationArray[index]);
};

export const validateFullValuationAllowance = ({
  columnName,
  existingRow,
  finData
}: {
  columnName: 'currentYearFullValuationAllowance' | 'priorYearFullValuationAllowance';
  existingRow: FinancialInfo | SubJurisdiction | undefined;
  finData: FinancialInfo[];
}): boolean => {
  const financialInfoExistingRow = existingRow && 'rowId' in existingRow ? existingRow : undefined;

  // new row or not a financial info row
  if (!financialInfoExistingRow) {
    return true;
  }

  const financialInfoFullValuationCheckboxStatus = finData.find(
    (financialInfoRow) =>
      financialInfoRow.columnName === columnName &&
      formatSubjurisdictionName(financialInfoRow.rowName) ===
        formatSubjurisdictionName(financialInfoExistingRow.rowName)
  )?.value;

  return typeof financialInfoFullValuationCheckboxStatus === 'undefined'
    ? true
    : financialInfoFullValuationCheckboxStatus === 0;
};

// eslint-disable-next-line complexity
export const validateCellValue = ({
  existingRow,
  value,
  level,
  step,
  headerName,
  subJurisdictionNamesSet,
  finData,
  entityJurisdiction
}: {
  existingRow: FinancialInfo | SubJurisdiction | undefined;
  value?: any;
  level: Level;
  step: Step;
  headerName: string;
  subJurisdictionNamesSet: Set<SubJurisdiction['name']>;
  finData: FinancialInfo[];
  entityJurisdiction?: Jurisdiction | undefined;
}) => {
  const isExistingRow = Boolean(existingRow);

  if (level === 'state') {
    switch (step) {
      case 'apportionment': {
        if (headerName === 'State') {
          return validateSubJurisdictionUserInput(value, subJurisdictionNamesSet);
        }

        break;
      }

      case 'apportionment-flagged': {
        if (headerName === 'State') {
          return validateSubJurisdictionUserInput(value, subJurisdictionNamesSet);
        }

        break;
      }

      case 'tax-effected': {
        if (headerName === 'State') {
          return validateSubJurisdictionUserInput(value, subJurisdictionNamesSet);
        }

        break;
      }

      case 'modifications': {
        if (headerName === 'Modification Type') {
          return value.toLowerCase() === 'permanent' || value.toLowerCase() === 'temporary';
        }

        if (headerName === 'State') {
          const subJurisdictionValidationArray = value.map((v: string) =>
            validateSubJurisdictionUserInput(v, subJurisdictionNamesSet)
          );
          return !subJurisdictionValidationArray.includes(false);
        }

        break;
      }

      case 'rtp':
        return isExistingRow;

      case 'deferred':
        if (headerName === 'State') {
          return validateSubJurisdictionUserInput(value, subJurisdictionNamesSet);
        }

        return isExistingRow;

      default:
        return true;
    }
  } else if (level === 'federal') {
    switch (step) {
      case 'rtp':
        return isExistingRow;

      case 'deferred':
        return isExistingRow;

      default:
        break;
    }
  } else if (level === 'valuation-allowances') {
    switch (step) {
      case 'adjustment':
        if (headerName === 'Jurisdiction') {
          return (
            validateSubJurisdictionUserInput(value, subJurisdictionNamesSet) ||
            entityJurisdiction?.name?.toLowerCase() === value?.toLowerCase()
          );
        }

        if (headerName === 'Beg Balance Gross' || headerName === 'Beg Balance Tax-Effected') {
          return validateFullValuationAllowance({
            columnName: 'priorYearFullValuationAllowance',
            existingRow,
            finData
          });
        }

        if (headerName === 'End Balance Gross' || headerName === 'End Balance Tax-Effected') {
          return validateFullValuationAllowance({
            columnName: 'currentYearFullValuationAllowance',
            existingRow,
            finData
          });
        }

        break;

      default:
        break;
    }
  }

  return true;
};
